﻿package com.mgacreative.hotelru.feature.reservation.ui.create

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.domain.model.Guest
import com.mgacreative.hotelru.domain.model.Reservation
import com.mgacreative.hotelru.domain.repository.GuestRepository
import com.mgacreative.hotelru.domain.repository.ReservationRepository
import com.mgacreative.hotelru.domain.repository.AgencyRepository
import com.mgacreative.hotelru.domain.model.Agency
import com.mgacreative.hotelru.core.tenant.TenantSession
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

data class CreateReservationUiState(
    val isLoading: Boolean = false,
    val isSuccess: Boolean = false,
    val isDirectCheckIn: Boolean = false,
    val errorMessage: String? = null,
    
    // Guest info
    val fullName: String = "",
    val phone: String = "",
    val email: String = "",
    val idPassportNo: String = "",
    val country: String = "Turkey", // Default country
    val birthDate: String = "",
    
    // Reservation info
    val checkInDate: String = Clock.System.todayIn(TimeZone.currentSystemDefault()).toString(),
    val checkOutDate: String = Clock.System.todayIn(TimeZone.currentSystemDefault()).toString(),
    val specialNotes: String = "",
    val selectedRoomType: String = "",
    val selectedRoomId: String = "", // In a full app, this would be selected from available rooms
    val selectedAgencyId: String? = null // Default is Walk-in
)

class CreateReservationViewModel(
    private val guestRepository: GuestRepository,
    private val reservationRepository: ReservationRepository,
    private val agencyRepository: AgencyRepository,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _agencies = MutableStateFlow<List<Agency>>(emptyList())
    val agencies: StateFlow<List<Agency>> = _agencies.asStateFlow()

    init {
        viewModelScope.launch {
            _agencies.value = agencyRepository.getAgencies()
        }
    }

    private val _uiState = MutableStateFlow(CreateReservationUiState())
    val uiState: StateFlow<CreateReservationUiState> = _uiState.asStateFlow()

    fun updateGuestInfo(
        fullName: String? = null,
        phone: String? = null,
        email: String? = null,
        idPassportNo: String? = null,
        country: String? = null,
        birthDate: String? = null
    ) {
        _uiState.update { state ->
            state.copy(
                fullName = fullName ?: state.fullName,
                phone = phone ?: state.phone,
                email = email ?: state.email,
                idPassportNo = idPassportNo ?: state.idPassportNo,
                country = country ?: state.country,
                birthDate = birthDate ?: state.birthDate
            )
        }
    }

    fun updateReservationInfo(
        checkInDate: String? = null,
        checkOutDate: String? = null,
        specialNotes: String? = null,
        selectedRoomType: String? = null,
        isDirectCheckIn: Boolean? = null,
        selectedAgencyId: String? = null
    ) {
        _uiState.update { state ->
            state.copy(
                checkInDate = checkInDate ?: state.checkInDate,
                checkOutDate = checkOutDate ?: state.checkOutDate,
                specialNotes = specialNotes ?: state.specialNotes,
                selectedRoomType = selectedRoomType ?: state.selectedRoomType,
                isDirectCheckIn = isDirectCheckIn ?: state.isDirectCheckIn,
                selectedAgencyId = if (selectedAgencyId == "CLEAR") null else (selectedAgencyId ?: state.selectedAgencyId)
            )
        }
    }

    fun createReservation() {
        val currentState = _uiState.value

        // Basic Validation
        if (currentState.fullName.isBlank() && currentState.selectedAgencyId == null) {
            _uiState.update { it.copy(errorMessage = "Lütfen misafir adını giriniz veya bir acenta seçiniz.") }
            return
        }
        if (currentState.checkInDate >= currentState.checkOutDate) {
            _uiState.update { it.copy(errorMessage = "Çıkış tarihi, giriş tarihinden sonra olmalıdır.") }
            return
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            val tenantId = tenantSession.currentTenantId

            if (tenantId == null) {
                _uiState.update { it.copy(isLoading = false, errorMessage = "Oturum hatası, tekrar giriş yapın.") }
                return@launch
            }

            try {
                // 1. Create or Find Guest
                val defaultFirstName = if (currentState.selectedAgencyId != null) "Acenta" else "Bilinmeyen"
                val defaultLastName = if (currentState.selectedAgencyId != null) "Misafiri" else "Misafir"
                
                val guestFirstName = currentState.fullName.split(" ").firstOrNull()?.takeIf { it.isNotBlank() } ?: defaultFirstName
                val guestLastName = currentState.fullName.split(" ").drop(1).joinToString(" ").takeIf { it.isNotBlank() } ?: defaultLastName

                val guest = Guest(
                    tenantId = tenantId,
                    firstName = guestFirstName,
                    lastName = guestLastName,
                    phone = currentState.phone.takeIf { it.isNotBlank() },
                    email = currentState.email.takeIf { it.isNotBlank() },
                    idPassportNo = currentState.idPassportNo.takeIf { it.isNotBlank() },
                    country = currentState.country.takeIf { it.isNotBlank() },
                    birthDate = currentState.birthDate.takeIf { it.isNotBlank() }?.let { dateStr ->
                        try {
                            val parts = dateStr.split('/', '.', '-').filter { it.isNotBlank() }
                            val formatted = if (parts.size == 3 && parts[2].length == 4) {
                                "${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}"
                            } else if (parts.size == 3 && parts[0].length == 4) {
                                "${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}"
                            } else {
                                null
                            }
                            if (formatted?.matches(Regex("""^\d{4}-\d{2}-\d{2}$""")) == true) formatted else null
                        } catch (e: Exception) {
                            null
                        }
                    }
                )

                val createdGuest = guestRepository.createGuest(guest)
                if (createdGuest == null || createdGuest.id == null) {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Misafir kaydı oluşturulamadı.") }
                    return@launch
                }

                // 2. Create Reservation
                val newReservation = Reservation(
                    tenantId = tenantId,
                    guestId = createdGuest.id!!,
                    roomId = currentState.selectedRoomId.takeIf { it.isNotBlank() },
                    checkInDate = currentState.checkInDate,
                    checkOutDate = currentState.checkOutDate,
                    status = if (currentState.isDirectCheckIn) "CHECKED_IN" else "CONFIRMED",
                    roomType = currentState.selectedRoomType.takeIf { it.isNotBlank() },
                    totalPrice = 0.0, // Should be calculated based on pricing
                    specialNotes = currentState.specialNotes.takeIf { it.isNotBlank() },
                    agencyId = currentState.selectedAgencyId
                )

                val createdReservation = reservationRepository.createReservation(newReservation)
                if (createdReservation != null) {
                    _uiState.update { it.copy(isLoading = false, isSuccess = true) }
                } else {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Rezervasyon oluşturulamadı.") }
                }

            } catch (e: Exception) {
                e.printStackTrace()
                _uiState.update { it.copy(isLoading = false, errorMessage = e.message ?: "Bilinmeyen bir hata oluştu.") }
            }
        }
    }
    
    fun resetState() {
        _uiState.update { CreateReservationUiState() }
    }
    
    fun clearError() {
        _uiState.update { it.copy(errorMessage = null) }
    }
}

